import * as mapboxgl from 'mapbox-gl';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Feature, Point } from 'geojson';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { createPointPopup } from './map-point-popup';
import {
  MapPointDraft,
  MapPointEditorComponent,
  MapPointEditorResult,
  pointAreaHa,
  pointAreaLabel,
  withPointArea,
} from './map-point-editor.component';
import { isValidPointArea } from './map-point.models';
import { uniqueFeatureName } from './map-feature-names';
import { HEAVY_FEATURE_LIMIT } from './map-editor.constants';

const POINTS_SOURCE_ID = 'map-points-src';
const POINTS_LAYER_ID = 'map-points-layer';
const POINT_COLOR = '#7c3aed';

interface AppliedMarkerState {
  lng: number;
  lat: number;
  label: string;
  area: string;
}

export interface MapPointsContext {
  map: () => mapboxgl.Map;
  draw: () => MapboxDraw;
  dialog: MatDialog;
  translate: (key: string) => string;
  changed: () => void;
  hint: (key: string) => void;
  resetHint: () => void;
  beforeOpen?: () => void;
  select?: (id: string) => void;
  detail?: (feature: Feature) => HTMLElement | null;
}

export class MapPointsController {
  private readonly markers = new Map<string, mapboxgl.Marker>();
  private picking: MapPointDraft | null = null;
  private editor?: MatDialogRef<MapPointEditorComponent, MapPointEditorResult>;
  private destroyed = false;
  private popup?: mapboxgl.Popup;
  private editingId?: string;
  private readonly applied = new Map<string, AppliedMarkerState>();
  private layerActive = false;
  constructor(private readonly context: MapPointsContext) {}
  features(): Feature<Point>[] {
    return (
      (this.context
        .draw()
        ?.getAll()
        .features.filter((f) => f.geometry.type === 'Point') as Feature<Point>[]) ?? []
    );
  }
  open(id?: string, draft?: MapPointDraft) {
    if (this.destroyed) return;
    this.context.beforeOpen?.();
    this.cancelPicking();
    this.context.draw().changeMode('simple_select');
    this.editor?.close();
    this.closePopup();
    const feature = id ? this.context.draw().get(id) : undefined;
    const point = feature?.geometry.type === 'Point' ? feature : undefined;
    const data: MapPointDraft = draft ?? {
      id,
      name: String(point?.properties?.['name'] ?? ''),
      lat: point?.geometry.type === 'Point' ? point.geometry.coordinates[1] : null,
      lng: point?.geometry.type === 'Point' ? point.geometry.coordinates[0] : null,
      areaHa: point ? pointAreaHa(point.properties) : null,
    };
    if (id && point && data.lat != null && data.lng != null) {
      this.editingId = id;
      this.popup = createPointPopup(
        this.context.map(),
        data,
        this.context.translate,
        (result) => {
          this.closePopup();
          this.handleResult(result);
        },
        this.context.detail?.(point)
      );
      return;
    }
    const ref = this.context.dialog.open(MapPointEditorComponent, {
      width: 'fit-content',
      maxWidth: '95vw',
      data,
      ariaLabel: this.context.translate('Map_add_point_tr'),
    });
    this.editor = ref;
    ref.afterClosed().subscribe((result) => {
      if (this.editor === ref) this.editor = undefined;
      this.handleResult(result);
    });
  }
  private closePopup() {
    this.popup?.remove();
    this.popup = undefined;
    this.editingId = undefined;
  }
  closeEditor() {
    this.cancelPicking();
    this.editor?.close();
    this.closePopup();
  }
  private handleResult(result?: MapPointEditorResult) {
    if (!result || this.destroyed) return;
    if (result.action === 'pick') {
      this.picking = result;
      this.context.map().getCanvas().style.cursor = 'crosshair';
      this.context.map().on('click', this.pick);
      document.addEventListener('keydown', this.onKey);
      this.context.hint('Map_point_pick_hint_tr');
      return;
    }
    if (
      result.lat == null ||
      result.lng == null ||
      !Number.isFinite(result.lat) ||
      !Number.isFinite(result.lng) ||
      Math.abs(result.lat) > 90 ||
      Math.abs(result.lng) > 180 ||
      !isValidPointArea(result.areaHa)
    )
      return;
    const existing = result.id ? this.context.draw().get(result.id) : undefined;
    if (result.id && !existing) return;
    this.context.draw().add({
      type: 'Feature',
      ...(result.id ? { id: result.id } : {}),
      properties: withPointArea(
        {
          ...existing?.properties,
          name: uniqueFeatureName(result.name, this.context.draw().getAll().features, result.id),
        },
        result.areaHa
      ),
      geometry: { type: 'Point', coordinates: [result.lng, result.lat] },
    });
    this.context.changed();
  }
  private pick = (event: mapboxgl.MapMouseEvent) => {
    const draft = this.picking;
    if (!draft) return;
    this.cancelPicking();
    const picked = { ...draft, lat: event.lngLat.lat, lng: event.lngLat.lng };
    if (!isValidPointArea(picked.areaHa)) {
      this.open(picked.id, picked);
      return;
    }
    this.handleResult({ ...picked, action: 'save' });
  };
  private onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') this.cancelPicking();
  };
  cancelPicking() {
    if (!this.picking) return;
    this.picking = null;
    this.context.map().off('click', this.pick);
    this.context.map().getCanvas().style.cursor = '';
    document.removeEventListener('keydown', this.onKey);
    this.context.resetHint();
  }
  refresh() {
    const features = this.features();
    const ids = new Set(features.map((f) => String(f.id)));
    if (this.editingId && !ids.has(this.editingId)) this.closePopup();
    if (features.length > HEAVY_FEATURE_LIMIT) {
      this.removeMarkers();
      this.renderLayer(features);
      return;
    }
    this.removeLayer();
    this.removeMarkersNotIn(ids);
    features.forEach((feature, index) => this.syncMarker(feature, this.labelOf(feature, index)));
  }
  private labelOf(feature: Feature<Point>, index: number): string {
    return String(feature.properties?.['name'] || `${this.context.translate('Map_point_tr')} ${index + 1}`);
  }
  private removeMarkersNotIn(ids: Set<string>) {
    for (const [id, marker] of this.markers)
      if (!ids.has(id)) {
        marker.remove();
        this.markers.delete(id);
        this.applied.delete(id);
      }
  }
  private removeMarkers() {
    for (const marker of this.markers.values()) marker.remove();
    this.markers.clear();
    this.applied.clear();
  }
  private syncMarker(feature: Feature<Point>, label: string) {
    const id = String(feature.id);
    const [lng, lat] = feature.geometry.coordinates;
    const marker = this.markers.get(id) ?? this.createMarker(id, lng, lat);
    const previous = this.applied.get(id);
    const area = pointAreaLabel(feature.properties);
    if (previous && previous.lng === lng && previous.lat === lat && previous.label === label && previous.area === area)
      return;
    if (previous && (previous.lng !== lng || previous.lat !== lat)) marker.setLngLat([lng, lat]);
    const element = marker.getElement();
    element.title = label;
    element.setAttribute('aria-label', `${label} – ${area}`);
    element.querySelector('[data-point-area]')!.textContent = area;
    this.applied.set(id, { lng, lat, label, area });
  }
  private createMarker(id: string, lng: number, lat: number): mapboxgl.Marker {
    const icon = document.createElement('button');
    icon.type = 'button';
    // Mapbox otherwise assigns role="img", hiding this interactive marker from button queries.
    icon.setAttribute('role', 'button');
    icon.style.cssText = `position:relative;width:20px;height:20px;padding:0;border:3px solid white;border-radius:50%;background:${POINT_COLOR};box-shadow:0 1px 6px rgba(0,0,0,.45);cursor:pointer;`;
    icon.appendChild(pointAreaTag());
    const marker = new mapboxgl.Marker({ element: icon, anchor: 'center' })
      .setLngLat([lng, lat])
      .addTo(this.context.map());
    const element = marker.getElement();
    element.style.cursor = 'pointer';
    element.addEventListener('click', (event) => {
      event.stopPropagation();
      this.focusPoint(id);
    });
    this.markers.set(id, marker);
    return marker;
  }
  private focusPoint(id: string) {
    const point = this.context.draw().get(id);
    if (point?.geometry.type !== 'Point') return;
    this.context.select?.(id);
    const map = this.context.map();
    map.flyTo({ center: point.geometry.coordinates as [number, number], zoom: Math.max(map.getZoom(), 15) });
  }
  private renderLayer(features: Feature<Point>[]) {
    const map = this.context.map();
    const data = {
      type: 'FeatureCollection' as const,
      features: features.map((feature) => ({
        type: 'Feature' as const,
        properties: { id: String(feature.id) },
        geometry: feature.geometry,
      })),
    };
    if (this.layerActive) {
      (map.getSource(POINTS_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(data);
      return;
    }
    map.addSource(POINTS_SOURCE_ID, { type: 'geojson', data });
    map.addLayer({
      id: POINTS_LAYER_ID,
      type: 'circle',
      source: POINTS_SOURCE_ID,
      paint: {
        'circle-color': POINT_COLOR,
        'circle-radius': 7,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 3,
      },
    });
    map.on('click', POINTS_LAYER_ID, this.onLayerClick);
    map.on('mouseenter', POINTS_LAYER_ID, this.onLayerEnter);
    map.on('mouseleave', POINTS_LAYER_ID, this.onLayerLeave);
    this.layerActive = true;
  }
  private removeLayer() {
    if (!this.layerActive) return;
    const map = this.context.map();
    map.off('click', POINTS_LAYER_ID, this.onLayerClick);
    map.off('mouseenter', POINTS_LAYER_ID, this.onLayerEnter);
    map.off('mouseleave', POINTS_LAYER_ID, this.onLayerLeave);
    if (map.getLayer(POINTS_LAYER_ID)) map.removeLayer(POINTS_LAYER_ID);
    if (map.getSource(POINTS_SOURCE_ID)) map.removeSource(POINTS_SOURCE_ID);
    map.getCanvas().style.cursor = '';
    this.layerActive = false;
  }
  private onLayerClick = (event: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }) => {
    const id = event.features?.[0]?.properties?.['id'];
    if (id != null) this.focusPoint(String(id));
  };
  private onLayerEnter = () => {
    this.context.map().getCanvas().style.cursor = 'pointer';
  };
  private onLayerLeave = () => {
    this.context.map().getCanvas().style.cursor = '';
  };
  destroy() {
    this.destroyed = true;
    this.cancelPicking();
    this.editor?.close();
    this.closePopup();
    this.removeMarkers();
    this.removeLayer();
  }
}

function pointAreaTag(): HTMLElement {
  const tag = document.createElement('span');
  tag.dataset['pointArea'] = '';
  tag.style.cssText = `position:absolute;top:19px;left:50%;transform:translateX(-50%);white-space:nowrap;font:600 12px system-ui,Arial,sans-serif;color:${POINT_COLOR};text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 2px #fff;pointer-events:none;`;
  return tag;
}
